import { useEffect, useRef, useState } from 'react';
import { useMap, Polyline, Polygon } from 'react-leaflet';
import L from 'leaflet';

/**
 * LassoControl — freehand polygon selection for any react-leaflet map.
 *
 * Drop it in as a child of a <MapContainer/>. It renders a toggle button
 * styled to match the native zoom control, and while armed it turns a
 * pointer drag into a closed polygon:
 *
 *   <MapContainer>
 *     <TileLayer ... />
 *     <LassoControl
 *       armed={armed}
 *       onArmedChange={setArmed}
 *       polygon={polygon}
 *       onComplete={setPolygon}
 *     />
 *   </MapContainer>
 *
 * Why hand-rolled: `leaflet-draw`'s polygon tool is click-to-place-vertices,
 * which is not a lasso. This is ~150 lines and costs no dependency (nor its
 * CSS and types).
 *
 * Pointer events, not mouse events — this repo is mobile-first and a
 * canvasser may be on a phone, so one code path has to serve mouse, touch,
 * and pen. We take pointer capture on the map container so a drag that
 * leaves the map still tracks and still terminates.
 *
 * The component owns only the *in-progress* trace. The committed ring is a
 * prop, so the parent decides what a selection means and when it clears.
 */

// lucide "lasso" glyph, inlined so the control needs no React inside the
// Leaflet DOM. currentColor picks up the control's link color.
const ICON_LASSO = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 22a5 5 0 0 1-2-4"/><path d="M3.3 14A6.8 6.8 0 0 1 2 10c0-4.4 4.5-8 10-8s10 3.6 10 8-4.5 8-10 8a12 12 0 0 1-5-1"/><path d="M5 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/></svg>`;

/**
 * Lasso ink. A literal, not `rgb(var(--accent))`: the accent token is red-600
 * and the map's densest tier dots are red-500 `anchor` voters, so an accent
 * lasso would read as data rather than as chrome. Sky reads as UI and does
 * not collide with any TIER_COLORS value.
 */
const LASSO_COLOR = '#38bdf8'; // sky-400

/** Drop pointermove samples closer than this (container px) to the last kept one. */
const MIN_PIXEL_STEP = 4;
/** Douglas-Peucker tolerance (container px) applied once, on commit. */
const SIMPLIFY_TOLERANCE = 2;
/** Hard vertex ceiling. The backend rejects >1000; stay well under it. */
const MAX_VERTICES = 500;
/** Below this a "drag" was really a tap — discard rather than commit a sliver. */
const MIN_RING_POINTS = 3;

export type LassoLatLng = { lat: number; lng: number };

export interface LassoControlProps {
  /** When true, a pointer drag draws instead of panning the map. */
  armed: boolean;
  /** Called to arm/disarm — including the self-disarm after a completed draw. */
  onArmedChange: (armed: boolean) => void;
  /** The committed ring, rendered as the settled polygon. Null hides it. */
  polygon: LassoLatLng[] | null;
  /** Fires once per completed draw, with a simplified ring in Leaflet order. */
  onComplete: (ring: LassoLatLng[]) => void;
  /** Leaflet control corner. Defaults to 'topleft', under the zoom control. */
  position?: L.ControlPosition;
}

/**
 * Thin a raw freehand trace down to a committable ring.
 *
 * A drag emits hundreds of near-collinear points. We simplify in *container
 * pixel* space rather than degrees, because that is the space the tolerance
 * is meaningful in — a degree is worth a different number of pixels at every
 * zoom. Safe to do here: the map cannot move mid-draw (we disable dragging),
 * so the pixel frame is the same one the points were captured in.
 */
function simplifyRing(map: L.Map, pts: LassoLatLng[]): LassoLatLng[] {
  if (pts.length < MIN_RING_POINTS) return [];

  let points = pts.map((p) => map.latLngToContainerPoint([p.lat, p.lng]));
  points = L.LineUtil.simplify(points, SIMPLIFY_TOLERANCE);

  // Douglas-Peucker is shape-preserving but has no vertex budget, so a very
  // wiggly lasso can still overrun the cap. Decimate evenly, keeping the last
  // point so the ring still closes where the user released.
  if (points.length > MAX_VERTICES) {
    const stride = Math.ceil(points.length / MAX_VERTICES);
    const kept = points.filter((_, i) => i % stride === 0);
    const last = points[points.length - 1];
    if (kept[kept.length - 1] !== last) kept.push(last);
    points = kept;
  }

  if (points.length < MIN_RING_POINTS) return [];

  return points.map((pt) => {
    const ll = map.containerPointToLatLng(pt);
    return { lat: ll.lat, lng: ll.lng };
  });
}

export function LassoControl({
  armed,
  onArmedChange,
  polygon,
  onComplete,
  position = 'topleft',
}: LassoControlProps) {
  const map = useMap();

  // The live trace. Mirrored into a ref because the pointer handlers are
  // registered once per arm and must read the current path without being
  // torn down and rebuilt on every sample.
  const [trace, setTrace] = useState<LassoLatLng[]>([]);
  const traceRef = useRef<LassoLatLng[]>([]);

  const drawingRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const lastPtRef = useRef<L.Point | null>(null);
  const buttonRef = useRef<HTMLAnchorElement | null>(null);

  // Latest-value refs so the control button and the pointer handlers can be
  // wired once and still call through to the current props.
  const armedRef = useRef(armed);
  armedRef.current = armed;
  const onArmedChangeRef = useRef(onArmedChange);
  onArmedChangeRef.current = onArmedChange;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // --- the toggle button -----------------------------------------------
  useEffect(() => {
    const control = new L.Control({ position });

    control.onAdd = () => {
      const bar = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
      const btn = L.DomUtil.create('a', '', bar) as HTMLAnchorElement;
      btn.href = '#';
      btn.setAttribute('role', 'button');
      btn.innerHTML = ICON_LASSO;
      // Center the SVG regardless of leaflet's default block sizing.
      btn.style.display = 'flex';
      btn.style.alignItems = 'center';
      btn.style.justifyContent = 'center';

      // Stop clicks from panning/zooming the map or following the href.
      L.DomEvent.disableClickPropagation(bar);
      L.DomEvent.on(btn, 'click', L.DomEvent.stop);
      // disableClickPropagation covers mousedown/touchstart, NOT pointerdown —
      // without this the button press would also start a lasso, because our
      // draw listener sits on the container this bar bubbles up to.
      L.DomEvent.on(bar, 'pointerdown', L.DomEvent.stopPropagation);
      L.DomEvent.on(btn, 'click', () => onArmedChangeRef.current(!armedRef.current));

      buttonRef.current = btn;
      return bar;
    };

    control.addTo(map);
    return () => {
      buttonRef.current = null;
      control.remove();
    };
  }, [map, position]);

  // Reflect armed state on the button.
  useEffect(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const label = armed ? 'Cancel lasso' : 'Lasso voters';
    btn.title = label;
    btn.setAttribute('aria-label', label);
    btn.setAttribute('aria-pressed', armed ? 'true' : 'false');
    btn.style.backgroundColor = armed ? LASSO_COLOR : '';
    btn.style.color = armed ? '#0f172a' : '';
  }, [armed]);

  // --- the draw interaction --------------------------------------------
  useEffect(() => {
    if (!armed) return;

    const container = map.getContainer();

    const cancelDraw = () => {
      drawingRef.current = false;
      const id = pointerIdRef.current;
      pointerIdRef.current = null;
      lastPtRef.current = null;
      if (id !== null && container.hasPointerCapture?.(id)) {
        try {
          container.releasePointerCapture(id);
        } catch {
          /* capture already gone — nothing to release */
        }
      }
      traceRef.current = [];
      setTrace([]);
    };

    const onPointerDown = (e: PointerEvent) => {
      // Left button only for mice; touch/pen report button 0 anyway.
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      drawingRef.current = true;
      pointerIdRef.current = e.pointerId;
      try {
        container.setPointerCapture(e.pointerId);
      } catch {
        /* capture unsupported — drags that leave the map just end there */
      }
      const pt = map.mouseEventToContainerPoint(e);
      lastPtRef.current = pt;
      const ll = map.containerPointToLatLng(pt);
      traceRef.current = [{ lat: ll.lat, lng: ll.lng }];
      setTrace(traceRef.current);
      e.preventDefault();
      e.stopPropagation();
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!drawingRef.current || e.pointerId !== pointerIdRef.current) return;
      const pt = map.mouseEventToContainerPoint(e);
      const last = lastPtRef.current;
      // Thin at capture time: bounds both memory and the React render count,
      // and costs the shape nothing at 4px.
      if (last && pt.distanceTo(last) < MIN_PIXEL_STEP) return;
      lastPtRef.current = pt;
      const ll = map.containerPointToLatLng(pt);
      traceRef.current = traceRef.current.concat({ lat: ll.lat, lng: ll.lng });
      setTrace(traceRef.current);
      e.preventDefault();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!drawingRef.current || e.pointerId !== pointerIdRef.current) return;
      // Read the path off the ref, not a setState updater — an updater can be
      // invoked twice under StrictMode, which would fire onComplete twice.
      const ring = simplifyRing(map, traceRef.current);
      cancelDraw();
      e.preventDefault();
      if (ring.length >= MIN_RING_POINTS) {
        onCompleteRef.current(ring);
        // One lasso per arming: a completed draw disarms so the very next
        // drag pans the map again, which is what a user expects.
        onArmedChangeRef.current(false);
      }
    };

    const onPointerCancel = (e: PointerEvent) => {
      if (e.pointerId !== pointerIdRef.current) return;
      cancelDraw();
    };

    // Esc backs out of an armed lasso without drawing.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      cancelDraw();
      onArmedChangeRef.current(false);
    };

    // A drag must not also pan/zoom the map under the cursor.
    const prev = {
      dragging: map.dragging.enabled(),
      doubleClickZoom: map.doubleClickZoom.enabled(),
      boxZoom: map.boxZoom.enabled(),
      touchZoom: map.touchZoom.enabled(),
    };
    map.dragging.disable();
    map.doubleClickZoom.disable();
    map.boxZoom.disable();
    map.touchZoom.disable();

    const prevCursor = container.style.cursor;
    const prevTouchAction = container.style.touchAction;
    container.style.cursor = 'crosshair';
    // Without this the browser claims the gesture and scrolls the page
    // instead of letting us draw. The single most important line on mobile.
    container.style.touchAction = 'none';

    // Keep popups from opening mid-drag. Clusters live in markerPane too.
    const markerPane = map.getPane('markerPane');
    const prevPanePointerEvents = markerPane?.style.pointerEvents ?? '';
    if (markerPane) markerPane.style.pointerEvents = 'none';

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerup', onPointerUp);
    container.addEventListener('pointercancel', onPointerCancel);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('pointercancel', onPointerCancel);
      document.removeEventListener('keydown', onKeyDown);

      cancelDraw();

      // Restore only what we actually took, in case a host map ships with
      // some of these already off.
      if (prev.dragging) map.dragging.enable();
      if (prev.doubleClickZoom) map.doubleClickZoom.enable();
      if (prev.boxZoom) map.boxZoom.enable();
      if (prev.touchZoom) map.touchZoom.enable();

      container.style.cursor = prevCursor;
      container.style.touchAction = prevTouchAction;
      if (markerPane) markerPane.style.pointerEvents = prevPanePointerEvents;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, armed]);

  return (
    <>
      {trace.length > 1 && (
        <Polyline
          positions={trace.map((p) => [p.lat, p.lng] as [number, number])}
          pathOptions={{
            color: LASSO_COLOR,
            weight: 2,
            dashArray: '4 4',
            interactive: false,
          }}
        />
      )}
      {polygon && polygon.length >= MIN_RING_POINTS && (
        <Polygon
          positions={polygon.map((p) => [p.lat, p.lng] as [number, number])}
          pathOptions={{
            color: LASSO_COLOR,
            weight: 2,
            fillColor: LASSO_COLOR,
            fillOpacity: 0.12,
            // Non-interactive so the settled shape doesn't swallow clicks on
            // the voter dots inside it.
            interactive: false,
          }}
        />
      )}
    </>
  );
}

export default LassoControl;
